import { Box, Paper, Typography, useTheme } from '@mui/material';
import { Speed as SpeedIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';

export interface RttDisplayProps {
  /** Round-trip time in milliseconds, or null if no measurement yet */
  rtt: number | null;
  /** Timestamp of the RTT measurement, in epoch milliseconds */
  measuredAt: number | null;
}

function formatFreshness(ageMs: number): string {
  const ageSeconds = Math.max(0, Math.floor(ageMs / 1000));

  if (ageSeconds < 5) {
    return 'just now';
  }

  if (ageSeconds < 60) {
    return `${ageSeconds}s ago`;
  }

  const ageMinutes = Math.floor(ageSeconds / 60);
  if (ageMinutes < 60) {
    return `${ageMinutes}m ago`;
  }

  const ageHours = Math.floor(ageMinutes / 60);
  if (ageHours < 24) {
    return `${ageHours}h ago`;
  }

  const ageDays = Math.floor(ageHours / 24);
  return `${ageDays}d ago`;
}

/**
 * Displays the latest API Round-Trip Time (RTT) at the bottom of the viewport.
 * Sticks to the bottom-right corner and shows RTT in milliseconds.
 */
export function RttDisplay({ rtt, measuredAt }: RttDisplayProps) {
  const theme = useTheme();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (rtt === null || measuredAt === null) {
      return;
    }

    const refreshNow = () => {
      setNow(Date.now());
    };

    const intervalId = window.setInterval(refreshNow, 30000);
    window.addEventListener('focus', refreshNow);
    document.addEventListener('visibilitychange', refreshNow);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshNow);
      document.removeEventListener('visibilitychange', refreshNow);
    };
  }, [rtt, measuredAt]);

  if (rtt === null || measuredAt === null) {
    return null; // Don't show anything if no RTT measurement yet
  }

  // Format RTT with appropriate precision
  const formattedRtt = rtt < 1 ? rtt.toFixed(2) : rtt.toFixed(0);
  const freshnessLabel = formatFreshness(now - measuredAt);

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        backgroundColor: 'background.paper',
        borderRadius: theme.shape.borderRadius,
        zIndex: theme.zIndex.snackbar,
        minWidth: 120,
      }}
    >
      <SpeedIcon
        sx={{
          fontSize: 20,
          color: theme.palette.primary.main,
        }}
      />
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.2 }}
        >
          RTT
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            lineHeight: 1.2,
          }}
        >
          {formattedRtt} ms
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.2 }}
          title={new Date(measuredAt).toLocaleString()}
        >
          Measured {freshnessLabel}
        </Typography>
      </Box>
    </Paper>
  );
}
